import { useClassName, useWindowSize } from 'common/hooks';
import React, { useState } from 'react';
import {
  ExternalLink,
  getContentfulAssetUrlandAlt,
  getContentfulField,
  SVGIcon,
} from 'common/components';
import './maps-and-location.less';
import { LinkTabs } from 'public/components/Tabs';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { EXPRESS_IMAGE_URL } from 'utils/env/client';
import { formatOpeningHours } from 'public/components/Header/HoursOfOperation/helpers';
import { Link } from 'react-router-dom';

export const MapsAndLocation = ({ venue }) => {
  const className = useClassName('MapsAndLocation');
  const [selectedLink, setSelectedLink] = useState(0);
  const { isMobile, isTablet } = useWindowSize();

  const mapsAndLocation = getContentfulField(venue.parkScreen?.mapsAndLocation);
  const title = getContentfulField(mapsAndLocation.fields.name);
  const settings = getContentfulField(mapsAndLocation.fields.settings);
  const description =
    getContentfulField(
      settings.find(
        (setting) => getContentfulField(setting.fields.key) === 'description'
      )?.fields.richTextValue
    ) || null;

  const slidesItems = getContentfulField(mapsAndLocation.fields.slides);

  const slides = slidesItems.map((slide) => {
    const name = getContentfulField(slide.fields.name);
    const type = slide.sys.contentType.sys.id;
    let desktop,
      mobile,
      tablet,
      googleMapsLink,
      appleMapsLink,
      iframeUrl,
      screenshot,
      address;

    if (type === 'mapLocation') {
      googleMapsLink = getContentfulField(slide.fields.googleMapsLink);
      appleMapsLink = getContentfulField(slide.fields.appleMapsLink);
      address = getContentfulField(slide.fields.address);
      const screenshotFields = getContentfulField(
        slide.fields.screenshot
      ).fields;
      screenshot = {
        desktop: getContentfulAssetUrlandAlt(screenshotFields.desktop),
        tablet: getContentfulAssetUrlandAlt(screenshotFields.tablet),
        mobile: getContentfulAssetUrlandAlt(screenshotFields.mobile),
      };
    } else if (type === 'lpFrame') {
      iframeUrl = getContentfulField(slide.fields.url);
    } else {
      desktop = getContentfulAssetUrlandAlt(slide.fields.desktop);
      mobile = getContentfulAssetUrlandAlt(slide.fields.mobile);
      tablet = getContentfulAssetUrlandAlt(slide.fields.tablet);
    }

    return {
      name,
      type,
      desktop,
      mobile,
      tablet,
      googleMapsLink,
      appleMapsLink,
      iframeUrl,
      screenshot,
      address,
    };
  });
  const links = slides.map((slide) => {
    return {
      url: '#',
      title: slide.name,
    };
  });

  const services =
    getContentfulField(
      getContentfulField(venue.parkScreen?.mapServices)?.fields?.items
    ) || [];

  const mappedServices = services.map((service) => {
    const icon = getContentfulField(
      getContentfulField(service.fields.icon)?.fields?.file
    )?.url;

    const links = service.fields.links
      ? getContentfulField(service.fields.links)
      : null;

    return {
      link: links?.[0],
      name: getContentfulField(service.fields.name),
      icon,
      description: getContentfulField(service.fields.description),
    };
  });

  const renderContent = (slide) => {
    if (slide.type === 'mapLocation') {
      let openingHoursString = formatOpeningHours(venue.openingHours);
      if (openingHoursString.includes(':')) {
        openingHoursString = openingHoursString
          .split(':')[1]
          .trim()
          .toUpperCase();
      }

      return (
        <div className={className('container__slides__slide__map-location')}>
          <div
            className={className(
              'container__slides__slide__map-location__info'
            )}>
            <h3
              className={className(
                'container__slides__slide__map-location__info__title'
              )}>
              Location
            </h3>
            <div
              className={className(
                'container__slides__slide__map-location__info__description'
              )}>
              <div
                className={className(
                  'container__slides__slide__map-location__info__description__icon'
                )}>
                <SVGIcon name="pin-icon" />
              </div>
              <div
                className={className(
                  'container__slides__slide__map-location__info__description__text'
                )}>
                <p>{slide.address}</p>
              </div>
            </div>
            <div
              className={className(
                'container__slides__slide__map-location__info__opening-hours'
              )}>
              <h4>{venue.name} Hours</h4>
              <p
                className={className(
                  'container__slides__slide__map-location__info__opening-hours__text'
                )}>
                {openingHoursString}
              </p>
              <p
                className={className(
                  'container__slides__slide__map-location__info__opening-hours__disclaimer'
                )}>
                *Opening hours may vary by establishment, please check our{' '}
                <Link to={`/venue/${venue.slug}`}>directory</Link> to plan your
                visit.
              </p>
            </div>
            <div
              className={className(
                'container__slides__slide__map-location__info__buttons'
              )}>
              <ExternalLink
                href={slide.googleMapsLink}
                className={className(
                  'container__slides__slide__map-location__info__buttons__button'
                )}>
                <SVGIcon name="google-icon" /> View on Google Maps
              </ExternalLink>
              <ExternalLink
                href={slide.appleMapsLink}
                className={className(
                  'container__slides__slide__map-location__info__buttons__button'
                )}>
                <SVGIcon name="apple-icon" /> View on Apple Maps
              </ExternalLink>
            </div>
          </div>
          <img
            src={slide.screenshot.desktop.assetUrl}
            alt={slide.screenshot.desktop.assetAlt}
          />
        </div>
      );
    }

    if (slide.type === 'lpFrame') {
      const DEFAULT_EXPRESS_IMAGE_URL =
        'https://ad.expressimage.digital/map-module/development/index.html';
      const jibestreamDestinationId = venue.jibestreamDestinationId;
      const url =
        slide.iframeUrl ||
        `${
          EXPRESS_IMAGE_URL || DEFAULT_EXPRESS_IMAGE_URL
        }?jibestreamId=${jibestreamDestinationId}`;

      return (
        <div
          className={className('container__slides__slide__frame')}
          dangerouslySetInnerHTML={{
            __html: `<iframe frameBorder={0} src=${url}></iframe>`,
          }}
        />
      );
    }

    if (isMobile && slide.mobile) {
      return <img src={slide.mobile.assetUrl} alt={slide.mobile.assetAlt} />;
    }

    if (isTablet && slide.tablet) {
      return <img src={slide.tablet.assetUrl} alt={slide.tablet.assetAlt} />;
    }

    return <img src={slide.desktop.assetUrl} alt={slide.desktop.assetAlt} />;
  };

  return (
    <div className={className('container__inner')} id="maps">
      <div className={className('container__header')}>
        <h2 className={className('container__header__title')}>{title}</h2>
        {description && (
          <div className={className('container__header__description')}>
            {documentToReactComponents(description)}
          </div>
        )}
      </div>
      <div className={className('container__links')}>
        <LinkTabs
          styles={{
            container: {
              backgroundColor: 'transparent',
            },
            linkClassName: className('container__links__link'),
          }}
          links={links}
          isSelected={(index) => index === selectedLink}
          fixed={false}
          onSelect={(e, index) => {
            e.preventDefault();
            setSelectedLink(index);
          }}
        />
      </div>
      <div className={className('container__slides')}>
        {slides.map((slide, index) => {
          const slideLength = slides.length;
          const depth = (index - selectedLink + slideLength) % slideLength;

          const zIndex = slideLength - depth;

          const translateY = -depth * 70;
          const scale = 1 - depth * 0.091;
          const opacity = 1 - depth * 0.12;

          return (
            <div
              className={className('container__slides__slide')}
              style={{
                zIndex,
                opacity,
                transform: `
                  translateY(${translateY}px)
                  scale(${scale})
                `,
              }}
              key={slide.name}>
              {renderContent(slide)}
            </div>
          );
        })}
      </div>
      <div className={className('container__services')}>
        {mappedServices.map((service) => {
          return (
            <div
              className={className('container__services__service')}
              key={service.name}>
              <div className={className('container__services__service__icon')}>
                <img src={service.icon} alt={service.name} />
              </div>
              <h3 className={className('container__services__service__title')}>
                {service.name}
              </h3>
              <div
                className={className(
                  'container__services__service__description'
                )}>
                {documentToReactComponents(service.description)}
              </div>
              <Link
                target={
                  getContentfulField(service.link.fields.type) === 'internal'
                    ? '_self'
                    : '_blank'
                }
                to={getContentfulField(service.link.fields.url)}
                className={className('container__services__service__button')}>
                {getContentfulField(service.link.fields.label)}
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
};
